// src/components/admin/BulkOrderManagement.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  IconButton,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Card,
  Chip,
  Badge,
  Menu,
  DialogContentText,
  Stack,
  Tooltip,
  styled,
  Popover,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  Search,
  Refresh,
  Close,
  Check,
  MoreVert,
  FilterList,
  Clear,
  Person,
  ShoppingBasket,
  AccessTime,
  CheckCircle,
  Cancel,
  Pending,
  Info,
  TrendingUp,
  AttachMoney,
  CalendarToday,
  Store,
} from '@mui/icons-material';
import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

// Import Bulk Order API hooks
import {
  useListBulkOrdersQuery,
  useGetBulkOrderCountQuery,
  useUpdateBulkOrderStatusMutation,
  useSetProductBulkOrderMutation,
  BulkOrder,
} from '../../features/admin/bulkOrderApi.ts';

// Styled Components for Responsive Design
const ResponsiveTableContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  overflowX: 'auto',
  '& .MuiTableCell-root': {
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('md')]: {
      padding: '8px 4px',
      fontSize: '12px',
    },
  },
}));

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const BulkOrderManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Get user role and vendor ID from Redux store
  const { role, vendorId, userName } = useSelector((state: any) => state.auth);
  const isVendor = role === 'vendor';

  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');

  // Dialog states
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedBulkOrder, setSelectedBulkOrder] = useState<BulkOrder | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);

  // Filter menu state
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);

  const ordersPerPage = 10;

  // Shopify-style color scheme
  const shopifyColors = {
    primary: '#008060',
    primaryHover: '#006e52',
    surface: '#f6f6f7',
    border: '#e1e3e5',
    textPrimary: '#202223',
    textSecondary: '#6d7175',
    success: '#008060',
    warning: '#ffc453',
    error: '#d72c0d',
    blue: '#2c6ecb',
    purple: '#5c6ac4',
    pending: '#ffc453',
    approved: '#008060',
    rejected: '#d72c0d',
  };

  // API hooks
  const {
    data: bulkOrdersResponse,
    isLoading,
    error,
    isError,
    refetch
  } = useListBulkOrdersQuery({
    page,
    limit: ordersPerPage,
    search_str: searchStr,
    status: statusFilter !== 'all' ? Number(statusFilter) : undefined,
    sortby: sortBy === 'newest' ? 'created_at' : sortBy === 'oldest' ? 'created_at' : 'product_quantity',
    sortorder: sortBy === 'newest' ? 'desc' : sortBy === 'oldest' ? 'asc' : 'desc',
  });

  const {
    data: countResponse,
    refetch: refetchCount
  } = useGetBulkOrderCountQuery({
    search_str: searchStr,
    status: statusFilter !== 'all' ? Number(statusFilter) : undefined,
  });

  const [updateBulkOrderStatus, { isLoading: isUpdating }] = useUpdateBulkOrderStatusMutation();
  const [setProductBulkOrder] = useSetProductBulkOrderMutation();

  // Handle API response structure
  const bulkOrders = bulkOrdersResponse?.result || bulkOrdersResponse?.data || [];
  const totalRecords = countResponse?.count || countResponse?.result?.count || bulkOrdersResponse?.count || 0;
  const totalPages = Math.ceil(totalRecords / ordersPerPage) || 1;

  // Auto-refresh count when search or filters change
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      refetchCount();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchStr, statusFilter, refetchCount]);

  // Check if any filter is active
  const isFilterActive = statusFilter !== 'all' || sortBy !== 'newest';

  // Handlers for pagination
  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchStr(value);
    setPage(1);
  };

  // Filter handlers
  const handleOpenFilterMenu = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilterMenu = () => {
    setFilterAnchorEl(null);
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleSortChange = (sort: string) => {
    setSortBy(sort);
    setPage(1);
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setSortBy('newest');
    setSearchStr('');
    setPage(1);
  };

  const handleRefresh = () => {
    refetch();
    refetchCount();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Detail dialog handlers
  const handleOpenDetailDialog = (order: BulkOrder) => {
    setSelectedBulkOrder(order);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedBulkOrder(null);
  };

  // Action dialog handlers
  const handleOpenActionDialog = (order: BulkOrder, type: 'approve' | 'reject') => {
    setSelectedBulkOrder(order);
    setActionType(type);
    setActionDialogOpen(true);
  };

  const handleCloseActionDialog = () => {
    setActionDialogOpen(false);
    setSelectedBulkOrder(null);
    setActionType(null);
  };

  const handleConfirmAction = async () => {
    if (!selectedBulkOrder || !actionType) return;

    try {
      const status = actionType === 'approve' ? 1 : 2;
      const result = await updateBulkOrderStatus({
        request_id: selectedBulkOrder.id || selectedBulkOrder.request_id || 0,
        status,
      }).unwrap();

      setSnackbar({
        open: true,
        message: result.message || `Bulk order request ${actionType === 'approve' ? 'approved' : 'rejected'} successfully!`,
        severity: 'success'
      });

      handleCloseActionDialog();
      refetch();
      refetchCount();
    } catch (err: any) {
      console.error('Failed to update bulk order status:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || err?.message || 'Error updating bulk order status. Please try again.',
        severity: 'error'
      });
    }
  };

  // Render status chip
  const renderStatus = (status: number) => {
    switch (status) {
      case 1:
        return (
          <Chip
            label="Approved"
            size="small"
            icon={<CheckCircle />}
            sx={{
              bgcolor: '#d1f4e0',
              color: shopifyColors.approved,
              fontWeight: 600,
              fontSize: isMobile ? '10px' : '12px',
              borderRadius: '6px'
            }}
          />
        );
      case 2:
        return (
          <Chip
            label="Rejected"
            size="small"
            icon={<Cancel />}
            sx={{
              bgcolor: '#fdeded',
              color: shopifyColors.rejected,
              fontWeight: 600,
              fontSize: isMobile ? '10px' : '12px',
              borderRadius: '6px'
            }}
          />
        );
      case 0:
      default:
        return (
          <Chip
            label="Pending"
            size="small"
            icon={<Pending />}
            sx={{
              bgcolor: '#ffe8d4',
              color: shopifyColors.pending,
              fontWeight: 600,
              fontSize: isMobile ? '10px' : '12px',
              borderRadius: '6px'
            }}
          />
        );
    }
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = 'AED') => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'symbol'
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status statistics
  const statusStats = useMemo(() => {
    const pending = bulkOrders.filter((order: BulkOrder) => order.status === 0).length;
    const approved = bulkOrders.filter((order: BulkOrder) => order.status === 1).length;
    const rejected = bulkOrders.filter((order: BulkOrder) => order.status === 2).length;
    return { pending, approved, rejected };
  }, [bulkOrders]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: { xs: '100%', md: '250px' }, flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            {/* Main Content Area */}
            <Box sx={{
              maxWidth: 1600,
              mx: 'auto',
              p: { xs: 1, sm: 2, md: 3 },
              width: '100%',
            }}>
              {/* Header Section */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                mb: 3,
                gap: { xs: 2, sm: 0 }
              }}>
                <Box>
                  <Typography variant="h5" sx={{
                    fontWeight: 600,
                    color: shopifyColors.textPrimary,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                  }}>
                    Bulk Order Requests
                  </Typography>
                  {isVendor && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Store fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                      <Typography variant="body2" sx={{
                        color: shopifyColors.textSecondary,
                        fontWeight: 500,
                        fontSize: { xs: '11px', sm: '12px' }
                      }}>
                        Vendor: {userName} (ID: {vendorId})
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                      borderRadius: '6px',
                      fontWeight: 500,
                      width: { xs: '100%', sm: 'auto' },
                      '&:hover': {
                        borderColor: shopifyColors.textSecondary,
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>

              {/* Statistics Cards */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2,
                mb: 3
              }}>
                <Card sx={{
                  p: 2,
                  borderRadius: '8px',
                  boxShadow: '0 1px 0 rgba(0, 0, 0, 0.05)',
                  border: `1px solid ${shopifyColors.border}`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '8px',
                      bgcolor: '#ffe8d4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <AccessTime sx={{ color: shopifyColors.pending, fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: shopifyColors.textPrimary }}>
                        {statusStats.pending}
                      </Typography>
                      <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                        Pending Requests
                      </Typography>
                    </Box>
                  </Box>
                </Card>

                <Card sx={{
                  p: 2,
                  borderRadius: '8px',
                  boxShadow: '0 1px 0 rgba(0, 0, 0, 0.05)',
                  border: `1px solid ${shopifyColors.border}`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '8px',
                      bgcolor: '#d1f4e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CheckCircle sx={{ color: shopifyColors.approved, fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: shopifyColors.textPrimary }}>
                        {statusStats.approved}
                      </Typography>
                      <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                        Approved
                      </Typography>
                    </Box>
                  </Box>
                </Card>

                <Card sx={{
                  p: 2,
                  borderRadius: '8px',
                  boxShadow: '0 1px 0 rgba(0, 0, 0, 0.05)',
                  border: `1px solid ${shopifyColors.border}`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '8px',
                      bgcolor: '#fdeded',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Cancel sx={{ color: shopifyColors.rejected, fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: shopifyColors.textPrimary }}>
                        {statusStats.rejected}
                      </Typography>
                      <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                        Rejected
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Box>

              {/* Search and Filter Bar */}
              <Card sx={{
                borderRadius: '8px',
                boxShadow: '0 1px 0 rgba(0, 0, 0, 0.05)',
                border: `1px solid ${shopifyColors.border}`,
                mb: 2,
                overflow: 'visible',
                width: '100%',
              }}>
                <Box sx={{
                  p: 2,
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  {/* Search Input with Filter Button */}
                  <Box sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    flex: 1,
                    minWidth: { xs: '100%', sm: '300px' }
                  }}>
                    <TextField
                      size="small"
                      placeholder="Search bulk orders..."
                      value={searchStr}
                      onChange={handleSearch}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                          bgcolor: 'white',
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                          </InputAdornment>
                        ),
                        endAdornment: searchStr ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setSearchStr('')}
                              edge="end"
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />

                 
                  </Box>

                  {/* Results Summary */}
                  {!isLoading && !isError && (
                    <Typography variant="body2" sx={{
                      color: shopifyColors.textSecondary,
                      ml: { xs: 0, sm: 'auto' },
                      textAlign: { xs: 'center', sm: 'right' },
                      width: { xs: '100%', sm: 'auto' }
                    }}>
                      {isFilterActive || searchStr ? (
                        `${totalRecords} bulk order${totalRecords === 1 ? '' : 's'} found`
                      ) : (
                        `${totalRecords} bulk order${totalRecords === 1 ? '' : 's'} total`
                      )}
                    </Typography>
                  )}
                </Box>
              </Card>

              {/* Loading State */}
              {isLoading && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 200,
                  bgcolor: 'white',
                  borderRadius: '8px',
                  border: `1px solid ${shopifyColors.border}`,
                  width: '100%',
                }}>
                  <CircularProgress sx={{ color: shopifyColors.primary }} />
                  <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>
                    Loading bulk orders...
                  </Typography>
                </Box>
              )}

              {/* Error State */}
              {isError && !isLoading && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    borderRadius: '8px',
                    border: `1px solid ${shopifyColors.border}`,
                    width: '100%',
                  }}
                  action={
                    <IconButton color="inherit" size="small" onClick={handleRefresh}>
                      <Refresh />
                    </IconButton>
                  }
                >
                  Failed to load bulk orders
                </Alert>
              )}

              {/* Empty State */}
              {!isLoading &&
                !isError &&
                bulkOrders.length === 0 && (
                  <Box sx={{
                    textAlign: 'center',
                    py: 8,
                    bgcolor: 'white',
                    borderRadius: '8px',
                    border: `1px solid ${shopifyColors.border}`,
                    width: '100%',
                  }}>
                    <ShoppingBasket sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2 }} />
                    <Typography variant="h6" sx={{ color: shopifyColors.textSecondary, fontWeight: 600, mb: 1 }}>
                      {searchStr || isFilterActive ? 'No bulk orders found' : 'No bulk orders available'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 3 }}>
                      {searchStr
                        ? `No bulk orders match your search "${searchStr}"`
                        : isFilterActive
                          ? 'No bulk orders match your filters'
                          : 'No bulk order requests have been submitted yet'
                      }
                    </Typography>
                    {(searchStr || isFilterActive) && (
                      <Button
                        variant="outlined"
                        startIcon={<Clear />}
                        onClick={handleClearFilters}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '6px',
                          mt: 1,
                          '&:hover': {
                            borderColor: shopifyColors.textSecondary,
                          }
                        }}
                      >
                        Clear Search & Filters
                      </Button>
                    )}
                  </Box>
                )}

              {/* Bulk Orders Table */}
              {!isLoading && bulkOrders.length > 0 && (
                <Card sx={{
                  borderRadius: '8px',
                  boxShadow: '0 1px 0 rgba(0, 0, 0, 0.05)',
                  border: `1px solid ${shopifyColors.border}`,
                  overflow: 'hidden',
                  width: '100%',
                }}>
                  <ResponsiveTableContainer>
                    <Box sx={{
                      minWidth: '1200px',
                      width: '100%',
                    }}>
                      {/* Table Header */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: isMobile
                          ? '60px 100px minmax(200px, 1fr) 120px'
                          : '60px 100px minmax(200px, 1fr) 150px 120px 120px 120px 150px 150px',
                        gap: { xs: 1, md: 2 },
                        px: { xs: 1.5, md: 3 },
                        py: 2,
                        borderBottom: `1px solid ${shopifyColors.border}`,
                        bgcolor: '#fafbfb',
                        width: '100%',
                      }}>
                        <Typography variant="caption" sx={{
                          color: shopifyColors.textSecondary,
                          fontWeight: 600,
                          fontSize: { xs: '10px', md: '11px' },
                          textTransform: 'uppercase',
                        }}>
                          #
                        </Typography>

                        <Typography variant="caption" sx={{
                          color: shopifyColors.textSecondary,
                          fontWeight: 600,
                          fontSize: { xs: '10px', md: '11px' },
                          textTransform: 'uppercase',
                        }}>
                          Image
                        </Typography>

                        <Typography variant="caption" sx={{
                          color: shopifyColors.textSecondary,
                          fontWeight: 600,
                          fontSize: { xs: '10px', md: '11px' },
                          textTransform: 'uppercase',
                        }}>
                          Product
                        </Typography>

                        {!isMobile && (
                          <>
                            <Typography variant="caption" sx={{
                              color: shopifyColors.textSecondary,
                              fontWeight: 600,
                              fontSize: { xs: '10px', md: '11px' },
                              textTransform: 'uppercase',
                            }}>
                              Customer
                            </Typography>

                            <Typography variant="caption" sx={{
                              color: shopifyColors.textSecondary,
                              fontWeight: 600,
                              fontSize: { xs: '10px', md: '11px' },
                              textTransform: 'uppercase',
                            }}>
                              Quantity
                            </Typography>

                            <Typography variant="caption" sx={{
                              color: shopifyColors.textSecondary,
                              fontWeight: 600,
                              fontSize: { xs: '10px', md: '11px' },
                              textTransform: 'uppercase',
                            }}>
                              Min Qty
                            </Typography>

                            <Typography variant="caption" sx={{
                              color: shopifyColors.textSecondary,
                              fontWeight: 600,
                              fontSize: { xs: '10px', md: '11px' },
                              textTransform: 'uppercase',
                            }}>
                              Status
                            </Typography>

                            <Typography variant="caption" sx={{
                              color: shopifyColors.textSecondary,
                              fontWeight: 600,
                              fontSize: { xs: '10px', md: '11px' },
                              textTransform: 'uppercase',
                            }}>
                              Created
                            </Typography>
                          </>
                        )}

                        <Typography variant="caption" sx={{
                          color: shopifyColors.textSecondary,
                          fontWeight: 600,
                          fontSize: { xs: '10px', md: '11px' },
                          textTransform: 'uppercase',
                          textAlign: 'right',
                        }}>
                          Actions
                        </Typography>
                      </Box>

                      {/* Table Rows */}
                      <Box sx={{ bgcolor: 'white' }}>
                        {bulkOrders.map((order: BulkOrder, index: number) => (
                          <Box
                            key={order.id || index}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: isMobile
                                ? '60px 100px minmax(200px, 1fr) 120px'
                                : '60px 100px minmax(200px, 1fr) 150px 120px 120px 120px 150px 150px',
                              gap: { xs: 1, md: 2 },
                              px: { xs: 1.5, md: 3 },
                              py: { xs: 1.5, md: 2.5 },
                              alignItems: 'center',
                              borderBottom: index < bulkOrders.length - 1 ? `1px solid ${shopifyColors.border}` : 'none',
                              '&:hover': { bgcolor: '#f9fafb' },
                              transition: 'background-color 0.15s ease',
                              width: '100%',
                            }}
                          >
                            {/* Row Number */}
                            <Typography variant="body2" sx={{
                              color: shopifyColors.textSecondary,
                              fontSize: { xs: '12px', md: '13px' },
                              textAlign: 'center',
                            }}>
                              {(page - 1) * ordersPerPage + index + 1}
                            </Typography>

                            {/* Product Image */}
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Avatar
                                src={order.product_main_image ? `https://fftestservice.sarsetech.com/images/${order.product_main_image}` : undefined}
                                variant="rounded"
                                sx={{
                                  width: { xs: 40, md: 48 },
                                  height: { xs: 40, md: 48 },
                                  bgcolor: '#f1f2f4',
                                  borderRadius: '6px'
                                }}
                              >
                                <ShoppingBasket />
                              </Avatar>
                            </Box>

                            {/* Product Info */}
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{
                                fontWeight: 600,
                                color: shopifyColors.textPrimary,
                                fontSize: { xs: '13px', md: '14px' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                                {order.product_name || order.prod_name || order.prod_short_name || 'N/A'}
                              </Typography>
                              <Typography variant="caption" sx={{
                                color: shopifyColors.textSecondary,
                                fontSize: { xs: '11px', md: '12px' },
                              }}>
                                Product ID: {order.product_id}
                              </Typography>

                              {/* Mobile View: Show condensed info */}
                              {isMobile && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography variant="caption" sx={{
                                    color: shopifyColors.textSecondary,
                                    fontSize: '10px',
                                    display: 'block',
                                  }}>
                                    Qty: {order.product_quantity} (Min: {order.min_qty})
                                  </Typography>
                                  {renderStatus(order.status)}
                                </Box>
                              )}
                            </Box>

                            {/* Desktop-only columns */}
                            {!isMobile && (
                              <>
                                {/* Customer Info */}
                                <Box>
                                  <Typography variant="body2" sx={{
                                    fontWeight: 600,
                                    color: shopifyColors.textPrimary,
                                    fontSize: '13px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}>
                                    {order.user_name || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" sx={{
                                    color: shopifyColors.textSecondary,
                                    fontSize: '11px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                  }}>
                                    {order.user_email || 'No email'}
                                  </Typography>
                                </Box>

                                {/* Quantity */}
                                <Box>
                                  <Typography variant="body2" sx={{
                                    fontWeight: 600,
                                    color: shopifyColors.textPrimary,
                                    fontSize: '14px'
                                  }}>
                                    {order.product_quantity}
                                  </Typography>
                                </Box>

                                {/* Min Quantity */}
                                <Box>
                                  <Chip
                                    label={`Min: ${order.min_qty}`}
                                    size="small"
                                    sx={{
                                      bgcolor: '#e6f6fc',
                                      color: shopifyColors.blue,
                                      fontWeight: 500,
                                      fontSize: '11px',
                                      borderRadius: '6px'
                                    }}
                                  />
                                  {order.max_qty > 0 && (
                                    <Typography variant="caption" sx={{
                                      color: shopifyColors.textSecondary,
                                      fontSize: '10px',
                                      display: 'block',
                                      mt: 0.5
                                    }}>
                                      Max: {order.max_qty}
                                    </Typography>
                                  )}
                                </Box>

                                {/* Status */}
                                <Box>
                                  {renderStatus(order.status)}
                                </Box>

                                {/* Created Date */}
                                <Box>
                                  <Typography variant="body2" sx={{
                                    color: shopifyColors.textSecondary,
                                    fontSize: '12px'
                                  }}>
                                    {formatDate(order.created_at)}
                                  </Typography>
                                </Box>
                              </>
                            )}

                            {/* Actions */}
                            <Box sx={{
                              display: 'flex',
                              gap: 0.5,
                              justifyContent: 'flex-end',
                              flexWrap: 'wrap',
                            }}>
                              {/* Mobile: Show simplified actions */}
                              {isMobile ? (
                                <>
                                  <Tooltip title="View Details">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenDetailDialog(order)}
                                      sx={{
                                        color: shopifyColors.textSecondary,
                                        '&:hover': {
                                          bgcolor: '#f1f2f4',
                                          color: shopifyColors.textPrimary
                                        }
                                      }}
                                    >
                                      <Info fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : (
                                // Desktop: Show all action buttons
                                <>
                                  {order.status === 0 && (
                                    <>
                                      <Tooltip title="Approve">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleOpenActionDialog(order, 'approve')}
                                          disabled={isUpdating}
                                          sx={{
                                            color: shopifyColors.success,
                                            '&:hover': {
                                              bgcolor: 'rgba(0, 128, 96, 0.1)',
                                            }
                                          }}
                                        >
                                          <Check fontSize="small" />
                                        </IconButton>
                                      </Tooltip>

                                      <Tooltip title="Reject">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleOpenActionDialog(order, 'reject')}
                                          disabled={isUpdating}
                                          sx={{
                                            color: shopifyColors.error,
                                            '&:hover': {
                                              bgcolor: 'rgba(215, 44, 13, 0.1)',
                                            }
                                          }}
                                        >
                                          <Close fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  )}

                                  <Tooltip title="View Details">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenDetailDialog(order)}
                                      sx={{
                                        color: shopifyColors.textSecondary,
                                        '&:hover': {
                                          bgcolor: '#f1f2f4',
                                          color: shopifyColors.textPrimary
                                        }
                                      }}
                                    >
                                      <Info fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </ResponsiveTableContainer>
                </Card>
              )}

              {/* Pagination */}
              {totalPages > 1 && !isLoading && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 3,
                  width: '100%',
                }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handleChangePage}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderRadius: '6px',
                        fontWeight: 500,
                        fontSize: '13px',
                      },
                      '& .Mui-selected': {
                        bgcolor: shopifyColors.primary + ' !important',
                        color: 'white',
                      },
                      '& .MuiPaginationItem-root:hover': {
                        bgcolor: '#f1f2f4',
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            margin: { xs: 1, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingBasket sx={{ color: shopifyColors.primary }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                Bulk Order Request Details
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDetailDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedBulkOrder && (
            <Stack spacing={3} sx={{ mt: 2 }}>
              {/* Product Information */}
              <Box sx={{ p: 2, bgcolor: '#fafbfb', borderRadius: '6px', border: `1px solid ${shopifyColors.border}` }}>
                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                  Product Information
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                  <Avatar
                    src={selectedBulkOrder.product_main_image ? `https://fftestservice.sarsetech.com/images/${selectedBulkOrder.product_main_image}` : undefined}
                    variant="rounded"
                    sx={{ width: 80, height: 80, borderRadius: '6px' }}
                  >
                    <ShoppingBasket />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                      {selectedBulkOrder.product_name || selectedBulkOrder.prod_name || selectedBulkOrder.prod_short_name || 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                      Product ID: {selectedBulkOrder.product_id}
                    </Typography>
                    {selectedBulkOrder.product_price && (
                      <Typography variant="body2" sx={{ color: shopifyColors.primary, fontWeight: 600, mt: 0.5 }}>
                        {formatCurrency(selectedBulkOrder.product_price, selectedBulkOrder.currency)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Customer Information */}
              <Box sx={{ p: 2, bgcolor: '#fafbfb', borderRadius: '6px', border: `1px solid ${shopifyColors.border}` }}>
                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                  Customer Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedBulkOrder.user_name || 'N/A'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, pl: 4 }}>
                    {selectedBulkOrder.user_email || 'No email provided'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, pl: 4 }}>
                    User ID: {selectedBulkOrder.user_id}
                  </Typography>
                </Box>
              </Box>

              {/* Order Details */}
              <Box sx={{ p: 2, bgcolor: '#fafbfb', borderRadius: '6px', border: `1px solid ${shopifyColors.border}` }}>
                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', mb: 2, display: 'block' }}>
                  Order Details
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                      Requested Quantity
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: shopifyColors.textPrimary }}>
                      {selectedBulkOrder.product_quantity}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                      Minimum Quantity
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: shopifyColors.blue }}>
                      {selectedBulkOrder.min_qty}
                    </Typography>
                  </Box>
                  {selectedBulkOrder.max_qty > 0 && (
                    <Box>
                      <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                        Maximum Quantity
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: shopifyColors.purple }}>
                        {selectedBulkOrder.max_qty}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                      Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {renderStatus(selectedBulkOrder.status)}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Timestamps */}
              <Box sx={{ p: 2, bgcolor: '#fafbfb', borderRadius: '6px', border: `1px solid ${shopifyColors.border}` }}>
                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                  Timeline
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                    <Typography variant="body2">
                      <strong>Created:</strong> {formatDate(selectedBulkOrder.created_at)}
                    </Typography>
                  </Box>
                  {selectedBulkOrder.updated_at && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                      <Typography variant="body2">
                        <strong>Updated:</strong> {formatDate(selectedBulkOrder.updated_at)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Actions for Pending Orders */}
              {selectedBulkOrder.status === 0 && (
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<Close />}
                    onClick={() => {
                      handleCloseDetailDialog();
                      handleOpenActionDialog(selectedBulkOrder, 'reject');
                    }}
                    sx={{
                      textTransform: 'none',
                      borderColor: shopifyColors.error,
                      color: shopifyColors.error,
                      '&:hover': {
                        borderColor: shopifyColors.error,
                        bgcolor: 'rgba(215, 44, 13, 0.04)',
                      }
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Check />}
                    onClick={() => {
                      handleCloseDetailDialog();
                      handleOpenActionDialog(selectedBulkOrder, 'approve');
                    }}
                    sx={{
                      bgcolor: shopifyColors.success,
                      textTransform: 'none',
                      '&:hover': { bgcolor: shopifyColors.primaryHover }
                    }}
                  >
                    Approve
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={handleCloseActionDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            margin: { xs: 1, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {actionType === 'approve' ? (
              <CheckCircle sx={{ color: shopifyColors.success }} />
            ) : (
              <Cancel sx={{ color: shopifyColors.error }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
              {actionType === 'approve' ? 'Approve' : 'Reject'} Bulk Order Request
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {actionType === 'approve' ? 'approve' : 'reject'} this bulk order request?
            {selectedBulkOrder && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#fafbfb', borderRadius: '6px' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  {selectedBulkOrder.product_name || selectedBulkOrder.prod_name || selectedBulkOrder.prod_short_name || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                  Customer: {selectedBulkOrder.user_name}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                  Quantity: {selectedBulkOrder.product_quantity} (Min: {selectedBulkOrder.min_qty})
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button
            onClick={handleCloseActionDialog}
            disabled={isUpdating}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: shopifyColors.textPrimary,
              borderRadius: '6px',
              px: 2.5
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={20} /> : (actionType === 'approve' ? <Check /> : <Close />)}
            sx={{
              bgcolor: actionType === 'approve' ? shopifyColors.success : shopifyColors.error,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '6px',
              px: 2.5,
              '&:hover': {
                bgcolor: actionType === 'approve' ? shopifyColors.primaryHover : '#b71c1c'
              }
            }}
          >
            {isUpdating ? 'Processing...' : (actionType === 'approve' ? 'Approve' : 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '6px',
            fontWeight: 500
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BulkOrderManagement;